// Records filter bar (architecture sprint-05 §6.2): user (active + Deactivated group), project (No
// project, active, Archived group), From/To with inline validation, and a Last 30 days reset.
import type { LocalDate } from '@shared/calendar';
import { useEffect, useState } from 'react';
import { rangeError } from '@/components/RangePicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import type { DateRange } from '@/lib/report-model';
import type { Project } from '@/types/projects';
import type { AdminUser } from '@/types/users';

interface RecordsFiltersProps {
  from: LocalDate;
  to: LocalDate;
  user: string;
  project: string;
  users: Array<Pick<AdminUser, 'id' | 'name' | 'status'>>;
  activeProjects: Project[];
  archivedProjects: Project[];
  onRangeChange: (range: DateRange) => void;
  onResetRange: () => void;
  onUserChange: (user: string) => void;
  onProjectChange: (project: string) => void;
  /** Sprint 11: the oversight page fixes the project, so its filter is hidden. */
  hideProject?: boolean;
}

function byName<T extends { name: string }>(a: T, b: T): number {
  return a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
}

export function RecordsFilters(props: RecordsFiltersProps): JSX.Element {
  const { from, to, user, project, users, activeProjects, archivedProjects } = props;
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraftFrom(from);
    setDraftTo(to);
    setError(null);
  }, [from, to]);

  function applyRange(nextFrom: string, nextTo: string): void {
    setDraftFrom(nextFrom);
    setDraftTo(nextTo);
    if (!nextFrom || !nextTo) return;
    const message = rangeError(nextFrom, nextTo);
    setError(message);
    if (message === null) props.onRangeChange({ from: nextFrom, to: nextTo });
  }

  const activeUsers = users.filter((u) => u.status === 'active').sort(byName);
  const deactivatedUsers = users.filter((u) => u.status === 'deactivated').sort(byName);

  return (
    <div className="card mb-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="grid gap-1.5">
          <Label htmlFor="records-user">User</Label>
          <NativeSelect id="records-user" value={user} onChange={(e) => props.onUserChange(e.target.value)}>
            <option value="all">All users</option>
            {activeUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
            {deactivatedUsers.length > 0 && (
              <optgroup label="Deactivated">
                {deactivatedUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </optgroup>
            )}
          </NativeSelect>
        </div>
        {!props.hideProject && (
        <div className="grid gap-1.5">
          <Label htmlFor="records-project">Project</Label>
          <NativeSelect id="records-project" value={project} onChange={(e) => props.onProjectChange(e.target.value)}>
            <option value="all">All projects</option>
            <option value="none">No project</option>
            {[...activeProjects].sort(byName).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
            {archivedProjects.length > 0 && (
              <optgroup label="Archived">
                {[...archivedProjects].sort(byName).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </optgroup>
            )}
          </NativeSelect>
        </div>
        )}
        <div className="grid gap-1.5">
          <Label htmlFor="records-from">From</Label>
          <Input id="records-from" type="date" value={draftFrom} onChange={(e) => applyRange(e.target.value, draftTo)} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="records-to">To</Label>
          <Input id="records-to" type="date" value={draftTo} onChange={(e) => applyRange(draftFrom, e.target.value)} />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="xs" onClick={props.onResetRange}>
          Last 30 days
        </Button>
        {error && (
          <p role="alert" className="text-xs text-danger-fg">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
